
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import NavigationBar from "@/components/landing/NavigationBar";
import Footer from "@/components/landing/Footer";
import { Loader2 } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const Payment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get parameters from the URL
  const priceId = searchParams.get('priceId');
  const planType = searchParams.get('planType');
  const isAnnual = searchParams.get('isAnnual') === 'true';

  useEffect(() => {
    const initializeCheckout = async () => {
      try {
        if (!priceId) {
          setError("Missing price ID");
          toast.error("Missing price ID");
          setTimeout(() => navigate('/'), 2000);
          return;
        }
        
        // Get the user session
        const { data, error: userError } = await supabase.auth.getSession();
        
        if (userError || !data.session) {
          console.error("Auth error:", userError);
          setError("Authentication error");
          toast.error("Authentication error, please login again");
          // Save checkout information before redirecting
          sessionStorage.setItem('pendingCheckout', JSON.stringify({ 
            priceId, 
            planName: planType,
            interval: isAnnual ? 'annual' : 'monthly' 
          }));
          setTimeout(() => navigate('/auth'), 2000);
          return;
        }
        
        // Extract user ID and email from session
        const userId = data.session.user.id;
        const userEmail = data.session.user.email;
        
        if (!userId || !userEmail) {
          setError("Invalid user data");
          toast.error("Invalid user data");
          setTimeout(() => navigate('/auth'), 2000);
          return;
        }
        
        // Make sure we have a plan type
        const finalPlanType = planType || (
          priceId.includes('starter') ? 'starter' :
          priceId.includes('pro') ? 'pro' :
          priceId.includes('premium') ? 'premium' : 'pay-per-report'
        );
        
        // Create a checkout session
        const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-simple-checkout', {
          body: {
            priceId,
            planType: finalPlanType,
            isAnnual,
            userId,
            userEmail,
            successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/`
          }
        });
        
        if (checkoutError) {
          console.error("Error creating checkout session:", checkoutError);
          setError("Checkout creation failed");
          toast.error("Failed to create checkout session");
          setTimeout(() => navigate('/'), 3000);
          return;
        }
        
        if (!checkoutData?.url) {
          setError("Invalid checkout response");
          toast.error("Invalid response from checkout service");
          setTimeout(() => navigate('/'), 3000);
          return;
        }
        
        // Redirect to Stripe checkout
        console.log("Redirecting to Stripe:", checkoutData.url);
        window.location.href = checkoutData.url;
      } catch (error) {
        console.error("Checkout error:", error);
        setError("Checkout initialization failed");
        toast.error("Failed to initialize checkout");
        setTimeout(() => navigate('/'), 3000);
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeCheckout();
  }, [priceId, planType, isAnnual, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
      <NavigationBar />
      
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <div className="bg-gradient-to-b from-gray-800/70 to-gray-900/70 backdrop-blur-sm p-8 rounded-3xl border border-blue-500/20 shadow-xl shadow-blue-500/10">
          <div className="flex justify-center mb-6">
            {isLoading ? (
              <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
              </div>
            ) : error ? (
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            ) : (
              <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
              </div>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-500">
            {error ? "Checkout Error" : "Preparing Checkout"}
          </h1>
          
          <p className="text-lg text-gray-300 mb-8">
            {error 
              ? error 
              : "Please wait while we redirect you to Stripe..."}
          </p>
          
          <Button 
            onClick={() => navigate('/')}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            Return to Home
          </Button>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Payment;
